import React, { useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "lib/utils";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "campaign-builder/ui";
import { useTargeting } from "./TargetingContext";
import { FieldError } from "./fields";

const CITIES_ERROR_MESSAGE = "Couldn't load cities. Please refresh and try again.";

// Searchable city dropdown (AC18). Placeholder "Select a city"; type-to-filter
// since the list can be long. Disabled while the city list loads or if it fails.
export const CitySelect = () => {
  const { cities, citiesLoading, citiesError, selectedCity, selectCity } = useTargeting();
  const [open, setOpen] = useState(false);

  // A failed city-list fetch leaves nothing to pick — disable the trigger and
  // surface the error rather than opening an empty dropdown.
  if (citiesError) {
    return (
      <>
        <button
          type="button"
          disabled
          aria-invalid
          data-cypress-id="targeting-city-select"
          className="flex h-9 w-full cursor-not-allowed items-center justify-between rounded-md border border-solid border-destructive bg-background px-3 py-2 text-sm text-muted-foreground opacity-70 shadow-sm"
        >
          <span className="truncate">Select a city</span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>
        <FieldError message={CITIES_ERROR_MESSAGE} />
      </>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          disabled={citiesLoading}
          data-cypress-id="targeting-city-select"
          className={cn(
            "flex h-9 w-full items-center justify-between rounded-md border border-solid border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          )}
        >
          <span className={cn("truncate", !selectedCity && "text-muted-foreground")}>
            {selectedCity ? selectedCity.nameWithState : "Select a city"}
          </span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search city..." />
          <CommandList>
            <CommandEmpty>No city found.</CommandEmpty>
            <CommandGroup>
              {cities.map((city) => (
                <CommandItem
                  key={city.id}
                  value={city.nameWithState}
                  data-cypress-id={`targeting-city-option-${city.id}`}
                  onSelect={() => {
                    selectCity(city);
                    setOpen(false);
                  }}
                >
                  <Check className={cn("mr-2 h-4 w-4", selectedCity?.id === city.id ? "opacity-100" : "opacity-0")} />
                  {city.nameWithState}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
